import { Box, Paper, Typography, IconButton, Tooltip, Badge } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountTree,
  ChevronLeft,
  HelpOutline
} from '@mui/icons-material';
import { usePanelStyles } from '@/styles/utilities/hooks';
import InputsBrowser from '../panels/InputsBrowser';

export default function LeftSidebar({
  // Panel state
  isLeftPanelCollapsed,
  setIsLeftPanelCollapsed,

  // Browser props
  allFMOInputs,
  allZipfiles,
  selectedNode,
  onNodeSelect,
  hasFilter,
  filteredZipfileCount,
  fmoInputsLoading,
  showBrowserHelp,
  onBrowserHelpToggle,
}) {
  const theme = useTheme();
  const panelStyles = usePanelStyles();

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar Button */}
      <Box sx={{
        width: theme.spacing(5), // 40px at standard density
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
      }}>
        <Tooltip 
          title={!isLeftPanelCollapsed ? 'Hide Browser' : 'Show Browser'} 
          placement="right"
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {!isLeftPanelCollapsed && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: theme.spacing(0.375), // 3px at standard density
                  height: theme.spacing(4), // 32px at standard density
                  bgcolor: 'primary.main',
                  borderRadius: `0 ${theme.spacing(0.25)} ${theme.spacing(0.25)} 0`,
                }}
              />
            )}
            <IconButton
              onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
              sx={{
                width: theme.spacing(5),
                height: theme.spacing(5),
                color: !isLeftPanelCollapsed ? 'primary.main' : 'text.secondary',
                bgcolor: !isLeftPanelCollapsed ? 'action.selected' : 'transparent',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
                borderRadius: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: theme.dimensions.icon.medium,
                },
              }}
            >
              {hasFilter ? (
                <Badge 
                  variant="dot" 
                  color="primary"
                  sx={{
                    '& .MuiBadge-dot': {
                      width: theme.spacing(0.75), // 6px at standard density
                      height: theme.spacing(0.75),
                      minWidth: theme.spacing(0.75),
                      borderRadius: '50%',
                    }
                  }}
                >
                  <AccountTree />
                </Badge>
              ) : (
                <AccountTree />
              )}
            </IconButton>
          </Box>
        </Tooltip>
      </Box>

      {/* Left Panel */}
      {!isLeftPanelCollapsed && (
        <Paper sx={{ 
          width: theme.dimensions?.panel?.leftSidebarWidth || 480, 
          minWidth: theme.dimensions?.panel?.leftSidebarMinWidth || 400,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          borderRight: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={panelStyles.draggableHeader}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={panelStyles.headerTitle}>
                Browser
              </Typography>
              <Tooltip title="Show help">
                <IconButton
                  size="small"
                  onClick={() => onBrowserHelpToggle(true)}
                  sx={{
                    padding: theme.spacing(0.5),
                    '& .MuiSvgIcon-root': {
                      fontSize: theme.dimensions.icon.small
                    }
                  }}
                >
                  <HelpOutline />
                </IconButton>
              </Tooltip>
            </Box>
            <Tooltip title="Collapse panel">
              <IconButton
                size="small"
                onClick={() => setIsLeftPanelCollapsed(true)}
                sx={{ padding: theme.spacing(0.5) }}
              >
                <ChevronLeft sx={{ fontSize: theme.dimensions.icon.small }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <InputsBrowser
              allFMOInputs={allFMOInputs}
              allZipfiles={allZipfiles}
              selectedNode={selectedNode}
              onNodeSelect={onNodeSelect}
              filteredZipfileCount={filteredZipfileCount}
              loading={fmoInputsLoading}
              showHelp={showBrowserHelp}
              onHelpToggle={onBrowserHelpToggle}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
}